import split from './bezier-split'
import { path as d3Path } from 'd3-path'
import { scaleLinear } from 'd3-scale'
import linePoints from 'line-points'

/*
 * Removes the command prefix from a command
 * e.g. escapeCommand('M12.98,45`) would just give us 12.98
 */
const escapeCommand = str => str.replace(/[^0-9\.]+/g, '')

/*
 * This class can be used to take a `path` and split it into a bunch of subpaths
 * based on the data `points` on the path.
 */
class Splitter {

  /*
   * Build the splitter that splits the path into individual paths for each point
   * in `points`.
   *
   * `scalers0`: The starting scalers
   * `scalers1`: Ending scalers, used if we scale points.
   *
   * `rangelookupscale`: used to lookup range ids based on a line's command points location. This is needed incase
   *      the scaler's range changed between transitions. See impact #41589
   */
  constructor (points, path, splitWidth, splitScale, scalers0, scalers1, rangelookupScale, filled = false) {
    this.scalers0 = scalers0
    this.scalers1 = scalers1

    this.points = points
    this.path = path

    this.splitWidth = splitWidth
    this.splitScale = splitScale
    this.rangelookupScale = rangelookupScale

    // Do some initialization work
    this.subpaths = (path.attr('d') || '').split('Z')
    this.filled = filled // filled path would be something like an area graph

    if (this.filled) {
      // Splitting provides an extra element with all contents after the last match
      // we can ignore it.
      this.subpaths.pop()
    }
  }

  getSplits () {
    let allSplits = this._getSplitMap()
    this.subpaths.forEach(subpath => {
      // Split this path to get all the commands
      let commands = subpath.split(/(?=[LMC])/)
                            .map(point => point.split(',')) // split coords into an array

      if (this.filled) {
        // Split commands into top/bottom, then parse
        let size = Math.floor(commands.length / 2)
          , [top, bottom] = [commands.slice(0, size), commands.slice(size, commands.length)]
          , topSplits = this._parseCommands(top)
          , bottomSplits = this._parseCommands(bottom)

        // Combine the top and bottom into a a new split map
        Object.keys(topSplits).forEach(rangeId => {
          if (topSplits[rangeId].points) {
            // Store the curves individually, removing the closing `z` commands
            // since we want to treat them as individual paths for the top curve and
            // bottom curves
            let split = topSplits[rangeId]

            if (!split.points.length) {
              // We don't have a curve to split
              return
            }

            split.curves = {
              top: {},
              bottom: {}
            }

            split.curves.top.path = split.curve.slice(0, -1)
            split.curves.bottom.path = bottomSplits[rangeId].curve.slice(0, -1)

            // Store points that make up the paths. These are used
            // to animate the path to some new location
            split.curves.top.pathPoints = linePoints(split.curves.top.path)
            split.curves.bottom.pathPoints = linePoints(split.curves.bottom.path)

            // Combine the top and bottom curves into a main curve
            // making up the full filled area
            split.points.push(...bottomSplits[rangeId].points)

            allSplits[rangeId] = split
          }
        })
      } else {
        // Basic unfillled line, parse the commands
        let splits = this._parseCommands(commands)

        Object.keys(splits).forEach(rangeId => {
          // Populate our master list of all splits if this rangeId has points
          if (splits[rangeId].points.length) {
            allSplits[rangeId] = splits[rangeId]
          }
        })
      }
    })

    this._fillGaps(allSplits)

    return Object.values(allSplits)
                 .map(split => {
                   split.curve = this._toCurve(split.points)

                   if (!this.filled) {
                     // Set the path points for the curve
                     split.pathPoints = linePoints(split.curve)
                   }
                   return split
                 })
  }

  /*
   * Scales the incoming points.
   *
   * `points`: The points on the svg path.
   * `point`: The data point used for the path.
   * `columnIdx`: The column index of the serie (used for x-axis positioning)
   * `hasHeight`: true if we need to scale the height serie
   * `range`: callback used to define the range on the scale. Used if we need to reverse the scaled points.
   */
  scalePoints (points, point, columnIdx, hasHeight = false, range) {
    let x = this.splitScale(point)
      , width = this.splitWidth
      , y = this.scalers1.yScale(Math.max(0, point._range[1]))
      , height = hasHeight ? Math.abs(this.scalers1.yScale(point._range[0]) - this.scalers1.yScale(point._range[1])) : 0

    if (isNaN(x)){
      // Probably a no breakdown chart. just use the middle point
      x = width / 2
    }

    let scale = scaleLinear().domain([points[0][0], points[points.length -1][0]])
                             .range(range ? range(x, width) : [x, x + width])

    return points.map(point => {
      return [scale(point[0]), y + height]
    })
  }

  /*
   * Builds an object where each key represents each of the actual
   * serie points (i.e. dots on the graph). Stores the original
   * point data as `_data`. The svg path control points will be
   * populated in an array `points`
   */
  _getSplitMap () {
    // We return an object containing all the actual serie points
    // (i.e. dots on the graph))
    return this.points.reduce((p, c) => {
      let id = c.rangeId
      if (id == undefined) {
        // No breakdown
        id = ''
      }
      p[id] = {}
      p[id].points = [] // An array of all the points making up the split path
      p[id]._data = c // Store the original data
      return p
    }, {})
  }

  /*
   * Turns an array of points into a proper svg path `d` attribute
   */
  _toCurve (points) {
    let path = d3Path()

    // Start at the first point in the path
    path.moveTo(points[0][0], points[0][1])

    for (let i = 0; i < points.length; i+=4) {
      if (this.filled && i === points.length / 2) {
        path.lineTo(points[i][0], points[i][1])
      }

      let cp0 = points[i + 1]
        , cp1 = points[i + 2]
        , cp = points[i + 3]

      path.bezierCurveTo(cp0[0], cp0[1], cp1[0], cp1[1], cp[0], cp[1])
    }

    if (this.filled) {
      path.closePath()
    }

    return path.toString()
  }

  /*
   * If any keys in splitPaths are missing points, that means
   * it's a single dot so a true path wasn't drawn. Force a path
   */
  _fillGaps (splits) {
    Object.keys(splits).forEach(rangeId => {
      let split = splits[rangeId]
      if (!split.points.length) {
        let x = this.scalers0.x.center(rangeId)
          , y = this.scalers0.yScale(split._data._range[1])

        if (isNaN(x)) {
          // Set x to the middle -- probably a no breakdown
          x = this.scalers0.x.groupScale.range()[1] / 2
        }

        split.points.push(...[
          [x - 1, y],
          [x + 1, y],
          [x + 1, y],
          [x + 1, y]
        ])

        split.curve = this._toCurve(split.points)

        if (this.filled) {
          // Filled, so set the top/bottom curves properties
          split.curves = {
            top: {
              pathPoints: split.points, // No need to parse the line, just use the points since it's a dot
              path: split.curve
            },
            bottom: {
              pathPoints: split.points,
              path: split.curve
            }
          }
        }
      }
    })
  }

  /*
   * Returns the rangeId associated with some `path` command point
   */
  _getRangeId (command) {
    if (!command[0]) {
      // Might be empty if this is a single point serie
      return null
    }
    let idx = Math.round(+escapeCommand(command[command.length - 2]) / this.rangelookupScale.x.groupScale.step())
    return this.scalers0.x.groupScale.domain()[idx]
  }

  /*
   * Parses a line running between command0 and command1
   */
  _parseLine (command0, command1) {
    let x1 = +escapeCommand(command0[0])
      , x2 = +escapeCommand(command1[0])
      , y1 = +command0[1]
      , y2 = +command1[1]
      , midX = (x1 + x2) / 2
      , midY = (y1 + y2) / 2
      , part0 = [ [x1, y1], [midX, midY], [midX, midY], [midX, midY] ]
      , part1 = [ [midX, midY], [x2, y2], [x2, y2], [x2, y2] ]

    return [part0, part1]
  }

  /*
   * Parses a curve running between command0 and command1
   */
  _parseCurve (command0, command1) {
    // It's a curve
    let startPoint = command0.length === 2 ? [command0[0], command0[1]] : [command0[4], command0[5]]
      , segment = [
        [+escapeCommand(startPoint[0]), +startPoint[1]],
        [+escapeCommand(command1[0]), +command1[1]],
        [+command1[2], +command1[3]],
        [+command1[4], +command1[5]]
      ]

    return split(segment)
  }

  /*
   * Parses the commands of a SVG path to build a map of all
   * the splits, key'd on the rangeId. Each object will have the points that make up
   * the curve of those commands and a `curve` property with the svg path `d` instructions.
   */
  _parseCommands (commands) {
    let splits = this._getSplitMap()

    commands.forEach((command, i, all) => {
      let next = all[i + 1] // next command
        , rangeId = this._getRangeId(command) || ''

      if (!next || next[0].startsWith('M')) {
        // not connected to another, ignored
        return
      }

      let nextRangeId = this._getRangeId(next)

      if (next[0].startsWith('L')) {
        // The next command is a basic line, so we can use the midpoint formula
        let parts = this._parseLine(command, next)
        splits[rangeId].points.push(...parts[0])
        splits[nextRangeId].points.push(...parts[1])
      } else if (next[0].startsWith('C')) {
        // Curve
        let parts = this._parseCurve(command, next)

        splits[rangeId].points.push(...parts[0])
        splits[nextRangeId].points.push(...parts[1])
      }
    })

    Object.values(splits).forEach(split => {
      if (split.points.length) {
        // Set the curve based on the points
        split.curve = this._toCurve(split.points)
      }
    })
    return splits
  }

}

export default Splitter
